import type { Property } from '@/modules/Project/Properties/types'
import { usePropertyDialog } from '@/modules/Project/usePropertyDialog'
import { isCollectionProperty, isSelectProperty } from '@/shared/utils/typeGuards'
import { until } from '@vueuse/core'
import { nextTick, toRaw, watch } from 'vue'
import { useProjectStore } from './useProjectStore'
import { useProperty } from './useProperty'

export const useOptimisticProperty = () => {
  const projectStore = useProjectStore()
  const propertyStore = useProperty()
  const { anchorRefsById, selectForGeneratedEditing } = usePropertyDialog()

  /**
   * Creates an optimistic property for preview mode
   */
  const createOptimisticProperty = async (
    baseProperty: Property,
    options: {
      name?: string
      insertPosition?: { afterId: string } | { atEnd: boolean }
    } = {},
  ) => {
    // Build a deep copy without reactive proxies (structuredClone fails on proxies)
    const property: Property = JSON.parse(JSON.stringify(toRaw(baseProperty)))

    // Fresh identifiers
    const newId = crypto.randomUUID()
    property.id = newId
    property.slug = newId

    // Set name if provided
    if (options.name) {
      property.name = options.name
    }

    // Mark as optimistic / unsaved
    property.isOptimistic = true

    // Reset sub-project linkage for collections so a fresh one is created on save
    if (property.type === 'collection' || property.type === 'file_collection') {
      property.config.subprojectConfig.child_project_id = ''
      property.config.subprojectConfig.primary_property_id = ''
    }

    // Clear hash (will be assigned by backend)
    property.hash = ''

    // Add property to store for header visibility
    projectStore.upsertProperty(property)

    // Insert into view at specified position
    if (projectStore.activeView?.view) {
      const ids = projectStore.activeView.view.propertyIds ?? []

      if (options.insertPosition && 'afterId' in options.insertPosition) {
        const originalIndex = ids.indexOf(options.insertPosition.afterId)
        if (originalIndex >= 0) {
          ids.splice(originalIndex + 1, 0, newId)
        } else {
          ids.push(newId)
        }
      } else {
        // Default to end
        ids.push(newId)
      }
    }

    const anchorEl = await until(() => anchorRefsById.value[newId]).toBeTruthy()
    selectForGeneratedEditing(property.id, anchorEl as HTMLElement)

    return property
  }

  /**
   * Seeds the property buffers for select/collection properties
   */
  const seedPropertyBuffers = async (property: Property) => {
    // Wait for watchers to settle
    await nextTick()

    // Re-seed option / sub-property buffers AFTER watchers have cleared them
    if (isSelectProperty(property)) {
      propertyStore.upsertOptions = property.config.options.map((o) => ({
        ...o,
        tool_fallback: o.tool_fallback || false,
      }))
    } else if (isCollectionProperty(property)) {
      propertyStore.upsertCollectionProperties = property.config.properties.map((p) => ({
        ...p,
      }))
    }
  }

  /**
   * Sets up cleanup watcher for optimistic properties
   */
  const setupOptimisticCleanup = (propertyId: string) => {
    const stopCleanup = watch(
      () => projectStore.selectedPropertyId,
      (currentId) => {
        if (currentId !== propertyId) {
          // Remove from store
          projectStore.removeProperty(propertyId)

          // Remove from view ids
          projectStore.views.forEach((v) => {
            if (v.propertyIds) {
              v.propertyIds = v.propertyIds.filter((pid) => pid !== propertyId)
            }
          })

          stopCleanup()
        }
      },
    )

    return stopCleanup
  }

  return {
    createOptimisticProperty,
    seedPropertyBuffers,
    setupOptimisticCleanup,
  }
}
